import { ErrorMessage, Input } from "@/components/UI";
import { Button } from "@/components/UI/Buttons/Button";
import Discount from "@/data/interfaces/Discount";
import adminService from "@/services/adminService";
import { FC, useEffect, useState } from "react";

const Discounts = () => {
  const [discounts, setDiscounts] = useState<Discount[] | undefined>();
  const [errorMessage, setErrorMsg] = useState<string>();

  useEffect(() => {
    adminService.discount.getAll().then((d) => setDiscounts(d));
  }, []);

  const toggleIsActive = async (discount: Discount) => {
    try {
      setErrorMsg(undefined);
      const newDisount: Discount = {
        ...discount,
        is_active: !discount.is_active,
      };

      await adminService.discount.updateDiscount(newDisount);
      setDiscounts(
        discounts?.map((d) => (d.code === newDisount.code ? newDisount : d)),
      );
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO IŠSAUGOTI PAKEITIMŲ ${discount.code}: ${error}`);
    }
  };

  const addNew = async (
    code: string,
    value: number,
    type: "percent" | "fixed",
    name: string,
    is_one_time: boolean,
  ) => {
    try {
      setErrorMsg(undefined);
      await adminService.discount.addNew(code, value, type, name, is_one_time);
      setDiscounts(await adminService.discount.getAll());
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO PRIDĖTI NAUJO: ${error}`);
    }
  };

  return (
    <>
      <ErrorMessage error={errorMessage} />
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Table discounts={discounts} toggleIsActive={toggleIsActive} />
        <NewDiscountCode onSubmit={addNew} />
      </div>
    </>
  );
};

export default Discounts;

const NewDiscountCode: FC<{
  onSubmit: (
    code: string,
    value: number,
    type: "percent" | "fixed",
    name: string,
    is_one_time: boolean,
  ) => object;
}> = ({ onSubmit }) => {
  const [code, setCode] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [type, setType] = useState<"percent" | "fixed">("percent");
  const [isOneTime, setIsOneTime] = useState<boolean>(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const numValue = parseFloat(value);
    if (!code || !name || !value || isNaN(numValue) || numValue <= 0) return;
    if (type === "percent" && numValue > 100) return;

    onSubmit(code, numValue, type, name, isOneTime);
    setCode("");
    setName("");
    setValue("");
    setType("percent");
    setIsOneTime(false);
  };

  return (
    <form className="flex max-w-[400px] flex-col" onSubmit={handleSubmit}>
      <h4 className="text-ink mb-4 text-center font-semibold">
        Pridėti naują:
      </h4>
      <Input
        label="Kodas"
        type="text"
        className="mb-4"
        value={code}
        onChange={(event) => setCode(event.target.value)}
        placeholder="pvz. SUMMER25"
      />
      <Input
        label="Pavadinimas"
        type="text"
        className="mb-4"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="pvz. Vasaros išpardavimas"
      />
      <div className="mb-4">
        <label className="text-ink/90 mb-2 block text-sm font-medium">
          Tipas
        </label>
        <select
          className="bg-sand border-line-strong text-ink w-full rounded-[10px] border px-3 py-2.5 text-[16px] outline-none focus:border-ink/25 focus:shadow-[0_0_0_2px_rgba(42,22,69,0.1)]"
          value={type}
          onChange={(e) => setType(e.target.value as "percent" | "fixed")}
        >
          <option value="percent">Procentinė nuolaida (%)</option>
          <option value="fixed">Fiksuota suma (€)</option>
        </select>
      </div>
      <Input
        label={type === "percent" ? "Procentas" : "Suma (€)"}
        type="number"
        step="0.01"
        min="0.01"
        max={type === "percent" ? "100" : undefined}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={type === "percent" ? "pvz. 25" : "pvz. 10.00"}
      />
      <div className="mt-4 flex items-center">
        <input
          type="checkbox"
          id="isOneTime"
          checked={isOneTime}
          onChange={(e) => setIsOneTime(e.target.checked)}
          className="mr-2 h-5 w-5 cursor-pointer"
        />
        <label
          htmlFor="isOneTime"
          className="text-ink/90 cursor-pointer text-sm"
        >
          Vienkartinis kodas (deaktyvuojamas po naudojimo)
        </label>
      </div>
      <Button type="submit" className="mt-8" size="sm">
        Išsaugoti
      </Button>
    </form>
  );
};

const Table: FC<{
  discounts?: Discount[];
  toggleIsActive: (discount: Discount) => void;
}> = ({ discounts, toggleIsActive }) => {
  const [dirtyCodes, setDirtyCodes] = useState<string[]>([]);

  const removeFromDirty = (code: string) => {
    return dirtyCodes.reduce((list, c) => {
      if (c !== code) list.push(c);
      return list;
    }, [] as string[]);
  };

  const changeIsActive = (code: string) => {
    if (dirtyCodes.includes(code)) {
      setDirtyCodes(removeFromDirty(code));
    } else {
      setDirtyCodes([...dirtyCodes, code]);
    }
  };

  const saveChanges = async (discount: Discount) => {
    await toggleIsActive(discount);
    setDirtyCodes(removeFromDirty(discount.code));
  };

  const formatDiscountValue = (discount: Discount) => {
    if (discount.type === "percent") {
      return `${discount.value}%`;
    } else {
      return `€${discount?.value?.toFixed(2)}`;
    }
  };

  return (
    <table className="text-ink h-min max-h-[500px] max-w-[700px] table-auto overflow-y-auto">
      <thead>
        <tr className="">
          <th className="text-muted border-line mb-2 w-auto border-b px-2 text-right text-sm font-semibold">
            Kodas
          </th>
          <th
            className="text-muted border-line mb-2 border-b px-2 text-sm font-semibold"
            style={{ maxWidth: "150px" }}
          >
            Pavadinimas
          </th>
          <th
            className="text-muted border-line mb-2 border-b px-2 text-sm font-semibold"
            style={{ maxWidth: "100px" }}
          >
            Nuolaida
          </th>
          <th
            className="text-muted border-line mb-2 border-b px-2 text-sm font-semibold"
            style={{ maxWidth: "100px" }}
          >
            Vienkartinis
          </th>
          <th
            className="text-muted border-line mb-2 border-b px-2 text-sm font-semibold"
            style={{ maxWidth: "100px" }}
          >
            Aktyvus
          </th>
          <th
            className="border-line mb-2 border-b px-2"
            style={{ width: "50px" }}
          ></th>
        </tr>
      </thead>
      <tbody className="overflow-y-auto">
        {discounts &&
          discounts.map((d) => {
            const isDirty = dirtyCodes.includes(d.code);
            const isGift = d.is_gift === true;

            return (
              <tr
                key={d.id}
                className={`${
                  isDirty ? "bg-brand/5 text-italic" : ""
                } text-center`}
              >
                <td className="mt-2 px-2 text-right">{d.code}</td>
                <td className="mt-2 px-2 text-sm">{d.name || "-"}</td>
                <td className="mt-2 px-2">{formatDiscountValue(d)}</td>
                <td className="mt-2 px-2">{d.is_one_time ? "Taip" : "Ne"}</td>
                <td
                  className={`mt-2 px-2 ${
                    !isGift
                      ? "cursor-pointer hover:font-semibold"
                      : "cursor-not-allowed opacity-50"
                  }`}
                  onClick={() => !isGift && changeIsActive(d.code)}
                  title={
                    isGift ? "Dovanų nuolaidos aktyvumo negalima keisti" : ""
                  }
                >
                  {isDirty
                    ? d.is_active
                      ? "Ne"
                      : "Taip"
                    : d.is_active
                      ? "Taip"
                      : "Ne"}
                </td>
                {isDirty && !isGift ? (
                  <td
                    className="mt-2 cursor-pointer px-2 text-center hover:font-semibold"
                    onClick={() => saveChanges(d)}
                  >
                    Išsaugoti
                  </td>
                ) : (
                  <td></td>
                )}
              </tr>
            );
          })}
      </tbody>
    </table>
  );
};
